import React, { createContext, useReducer, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import type { AuthUser, AuthState } from '../types/chat';
import { authService } from '../services/authService';

export interface AuthContextType extends AuthState {
  loginWithGoogle: () => void;
  logout: () => void;
  handleCallback: () => Promise<void>;
  isAdmin: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Export context for the hook to use
export { AuthContext };

type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_AUTHENTICATED'; payload: { user: AuthUser; token: string } }
  | { type: 'SET_UNAUTHENTICATED' }
  | { type: 'SET_USER'; payload: AuthUser };

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_AUTHENTICATED':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        token: action.payload.token,
        isLoading: false,
      };
    case 'SET_UNAUTHENTICATED':
      return {
        ...state,
        isAuthenticated: false,
        user: undefined,
        token: undefined,
        isLoading: false,
      };
    case 'SET_USER':
      return { ...state, user: action.payload };
    default:
      return state;
  }
};

const initialState: AuthState = {
  isAuthenticated: false,
  isLoading: true,
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const navigate = useNavigate();

  // Initialize authentication state
  useEffect(() => {
    const initializeAuth = async () => {
      dispatch({ type: 'SET_LOADING', payload: true });

      const token = authService.getToken();

      if (token && authService.isAuthenticated()) {
        try {
          const user = await authService.getCurrentUser();
          if (user) {
            dispatch({ type: 'SET_AUTHENTICATED', payload: { user, token } });
          } else {
            dispatch({ type: 'SET_UNAUTHENTICATED' });
          }
        } catch (error) {
          console.error('Failed to initialize auth:', error);
          dispatch({ type: 'SET_UNAUTHENTICATED' });
        }
      } else {
        dispatch({ type: 'SET_UNAUTHENTICATED' });
      }
    };

    initializeAuth();
  }, []);

  // Update Sentry user context when authentication state changes
  useEffect(() => {
    if (state.isAuthenticated && state.user) {
      // Set user context for error tracking
      Sentry.setUser({
        id: state.user.id,
        email: state.user.email,
        username: state.user.name,
      });
    } else {
      // Clear user context on logout
      Sentry.setUser(null);
    }
  }, [state.isAuthenticated, state.user]);

  const loginWithGoogle = () => {
    authService.loginWithGoogle();
  };

  const logout = () => {
    authService.logout();
    dispatch({ type: 'SET_UNAUTHENTICATED' });
  };

  const handleCallback = async () => {
    dispatch({ type: 'SET_LOADING', payload: true });

    const token = authService.handleCallback();

    if (token) {
      try {
        const user = await authService.getCurrentUser();
        if (user) {
          dispatch({ type: 'SET_AUTHENTICATED', payload: { user, token } });

          // Check for intended URL and redirect there, otherwise go to home
          const intendedUrl = sessionStorage.getItem('intendedUrl');
          if (intendedUrl) {
            sessionStorage.removeItem('intendedUrl'); // Clean up
            navigate(intendedUrl, { replace: true });
          } else {
            navigate('/', { replace: true });
          }
        } else {
          dispatch({ type: 'SET_UNAUTHENTICATED' });
        }
      } catch (error) {
        console.error('Failed to handle callback:', error);
        dispatch({ type: 'SET_UNAUTHENTICATED' });
      }
    } else {
      dispatch({ type: 'SET_UNAUTHENTICATED' });
    }
  };

  const isAdmin = () => {
    return authService.isAdmin();
  };

  const value: AuthContextType = {
    ...state,
    loginWithGoogle,
    logout,
    handleCallback,
    isAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
